import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import { formatNumber } from "libphonenumber-js";
import Form from "./Form";
import Actions from "../../../../components/NTable/Actions";
import Drawer from "../../../../components/Drawer";
import Topbar from "../../components/Topbar";
import { useManageForm } from "../../helpers/useManageForm";
import { useDrawerProps } from "../../helpers/useDrawerProps";
import useSettingsCRUDHook from "../../hook";
import { useFetchResource } from "../../helpers/useFetchResource";
import { useColumns } from "./columns";
import { useAccess, useActionWithAccess } from "../../../../utils/hooks/access";
import { useCompany } from "../../../../utils/hooks/company";
import {useVisibleColumnKeys} from "../../helpers/useVisibleColumnKeys";
import {useOnExport} from "../../helpers/useOnExport";
import {useImportModal} from "../../helpers/useImportModal";
import {useNewTable} from "../../../../components/NTable/hook";
import NTable from "../../../../components/NTable";
import {useQueryFilters} from "../../helpers/useQueryFilters";

function WhitelistNumber() {
	const { t } = useTranslation();
	const company = useCompany();

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		createSubmit,
		updateSubmit,
		goToHelp,
		goToView,
		goToCreate,
		goToEdit,
		settingsExport,
		loadingExport,
		setData,
	} = useSettingsCRUDHook("/whitelist-number");

	const { hasAccess: canAdd } = useAccess("whitelistNumber.canCreate");

	const { unarchiveModal, isFormOpen, formProps } = useManageForm({
		mode,
		selected,
		createSubmit,
		updateSubmit,
	});

	const columns = useColumns({
        edit: useActionWithAccess(goToEdit, "whitelistNumber.canCreate")
    });
    const table = useNewTable({
        data,
        columns,
        loading,
        totalItems,
        sessionFiltersKey: "whitelistNumbers",
        enableRowSelection: false,
    });

    const queryFilters = useQueryFilters(table);

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		help: t("help"),
		view: t("view-number"),
		create: t("add-number"),
		edit: t("edit-number"),
	});

	useFetchResource({ fetch, state: table.getState() });

    const visibleColumnKeys = useVisibleColumnKeys(table);

    const onExport = useOnExport({
        url: "/export/whitelist-number",
        exportCall: settingsExport,
        queryFilters,
        visibleColumnKeys
    });

    const { open: openImportModal, modal: importModal } = useImportModal({
        importUrl: "/whitelist-number/import",
        exportUrl: "/export/whitelist-number",
        setData,
        exportCall: settingsExport,
        loading: loading || loadingExport,
    });

	return (
		<div className="d-flex h-100">
			<Card className="flex-fill mb-0">
				<Topbar
					title="Whitelist numbers"
					add={canAdd && goToCreate}
					help={goToHelp}
					importFile={openImportModal}
					onExport={onExport}
					loading={loading || loadingExport}
				/>

				<Card
					className="mb-auto"
					style={{ maxHeight: "calc(100% - 156px)" }}
				>
                    <Actions
						filters={table.getState().columnFilters}
						setFilters={table.setColumnFilters}
						columns={table.getAllColumns()}
                    >
                        {company?.settings?.twilio?.phoneNumbers?.map(
                            (number) => (
                                <div className="text-muted text-sm">
                                    {formatNumber(
                                        number.toString(),
                                        "INTERNATIONAL",
                                    )}
                                </div>
                            ),
                        )}
                    </Actions>

                    <NTable table={table} />
				</Card>
			</Card>

			<Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
				{isFormOpen ? (
					<Form
						{...formProps}
						error={error}
						loading={submitting}
						close={goToView}
					/>
				) : (
					<>{t("help")}</>
				)}
			</Drawer>

            {importModal}

			{unarchiveModal}
		</div>
	);
}

export default WhitelistNumber;
